/**
 * Éléments communs aux écrans de réglages comptables : bandeau « à valider par l'expert-comptable »
 * (non masquable), notifications injectables, aides de rendu. Charte inchangée : classes et jetons existants.
 */
import { ToastService } from '@presentation/components/Toast';
import { confirmAction, type ConfirmActionOptions } from '@presentation/components/ConfirmDialog';
import { esc } from '@presentation/shell/format';

export const TEXTE_BANDEAU_EXPERT =
  "À valider par l'expert-comptable. Ces comptes (ou taux) sont les valeurs par défaut du logiciel ; ils n'ont pas encore été confirmés. Ne les figez pas dans vos documents clients avant sa réponse.";

export interface Notifier {
  success(message: string): void;
  error(message: string): void;
  warning(message: string): void;
  info(message: string): void;
}

export const notifierToast: Notifier = {
  success: (m) => ToastService.success(m),
  error: (m) => ToastService.error(m),
  warning: (m) => ToastService.warning(m),
  info: (m) => ToastService.info(m),
};

export type Confirmer = (options: ConfirmActionOptions) => Promise<boolean>;
export const confirmerDialogue: Confirmer = (options) => confirmAction(options);

/** Bandeau d'avertissement existant (`license-banner reminder`), jamais masquable : aucun bouton de fermeture. */
export function bandeauExpertHtml(): string {
  return `<div class="license-banner reminder" role="note" data-bandeau-expert>
    <span aria-hidden="true">⚠ </span><strong>${esc(TEXTE_BANDEAU_EXPERT)}</strong>
  </div>`;
}

export const STYLE_AIDE = 'font-size:12px;color:var(--ardoise);margin:0 0 12px;line-height:1.5;';
/** En-tête de LIGNE (th scope="row") : lisible comme une cellule normale, pas dans le style des en-têtes de colonne. */
export const STYLE_TH_LIGNE =
  'text-align:left;font-weight:600;font-size:13px;text-transform:none;letter-spacing:0;background:transparent;color:var(--marine);border-bottom:1px solid #EEF1F3;';
export const STYLE_SCROLL = 'overflow-x:auto;max-width:100%;';

/** Message d'erreur lié à un champ : annoncé aux lecteurs d'écran, rendu dans le style d'erreur existant. */
export function erreurChampHtml(id: string, message: string | undefined): string {
  return `<span class="field-error" id="${esc(id)}" role="alert" style="display:${message ? 'block' : 'none'};font-size:12px;color:var(--danger);font-weight:600;margin-top:4px;">${esc(message ?? '')}</span>`;
}

export function majErreurChamp(
  host: HTMLElement,
  champ: HTMLInputElement | HTMLSelectElement | null,
  erreurId: string,
  message: string | undefined,
): void {
  const zone = host.querySelector<HTMLElement>(`#${CSS.escape(erreurId)}`);
  if (zone) {
    zone.textContent = message ?? '';
    zone.style.display = message ? 'block' : 'none';
  }
  if (champ) {
    if (message) {
      champ.setAttribute('aria-invalid', 'true');
      champ.style.borderColor = 'var(--danger)';
    } else {
      champ.removeAttribute('aria-invalid');
      champ.style.borderColor = '';
    }
  }
}

export function majResume(zone: HTMLElement | null, message: string | null): void {
  if (!zone) return;
  zone.textContent = message ?? '';
  zone.hidden = !message;
}

/** Zone d'état poli (non intrusive) pour les confirmations non bloquantes. */
export function majStatut(zone: HTMLElement | null, message: string): void {
  if (zone) zone.textContent = message;
}
